<template>
  <qx-stand-alone id="all-content">
    <template #title>
      <h2>
        <qx-tooltip
          v-if="metadata.isDownloadEncrypted"
          :text="string.encryptedTootlip"
          location="top">
          <template #content="{ props }">
            <em
              v-bind="props"
              class="qx-icon icon-lock" />
            {{ title }}
          </template>
        </qx-tooltip>
      </h2>
    </template>
    <template #details>
      <p
        v-if="metadata.subject"
        id="subject">
        {{ metadata.subject }}
      </p>
      <share-message
        v-if="metadata.message"
        :message="metadata.message" />
      <div class="download-controls d-flex align-items-center">
        <qx-button
          v-if="metadata.folderId"
          id="go-to-quatrix"
          class="mr-2"
          state="border-blue"
          :action="onOpenInQuatrixClick">
          <template #button-content>
            <em class="qx-icon icon-files" />{{ string.goToQuatrixLabel }}
          </template>
        </qx-button>
        <div
          v-if="!decryptionActive"
          id="download-buttons">
          <qx-button
            v-if="files.length && isDownloadAllowed"
            id="download-all"
            class="mr-2"
            state="active-blue"
            :action="onDownloadAllFilesClick">
            <template #button-content>
              <div class="d-flex align-center">
                <em class="qx-icon icon-download" />
                <span>{{ string.downloadAllLabel }}</span>
              </div>
            </template>
          </qx-button>
          <template v-if="selectedFiles.length > 0">
            <qx-button
              id="download-selected"
              state="blue-text-hover-fill"
              :action="() => downloadSelectedFiles(selectedFiles)">
              <template #button-content>
                <div class="d-flex align-center">
                  <em class="qx-icon icon-download" />
                  <span>{{ string.downloadSelectedLabel }}</span>
                </div>
              </template>
            </qx-button>
            <qx-button
              v-if="isPreviewAllowed"
              id="preview-selected"
              state="blue-text-hover-fill"
              :action="previewSelected">
              <template #button-content>
                <div class="d-flex align-center">
                  <em class="qx-icon icon-eye-open" />
                  <span>{{ string.previewSelected }}</span>
                </div>
              </template>
            </qx-button>
          </template>
        </div>
      </div>
    </template>
    <qx-spinner
      v-if="spinnerId"
      :id="spinnerId" />
    <template v-if="!spinnerId && files.length">
      <keep-alive>
        <component
          :is="filesView"
          :items="files"
          :is-encrypted="decryptionActive"
          @change="handleChangeSelected"
          @download="downloadSelectedFiles"
          @toggle="toggleView" />
      </keep-alive>
    </template>
    <empty-list v-if="!spinnerId && !files.length" />
    <preview-files
      v-if="isPreviewActive"
      ref="previewFiles"
      :is-share-preview="true"
      :selected-ids="selectedFiles"
      :collection="files"
      :get-allowed-actions="getAllowedActions"
      @close="isPreviewActive = false" />
  </qx-stand-alone>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';

import QxButton from '@/elements/button';
import QxTooltip from '@/elements/QxTooltip';
import QxSpinner from '@/elements/spinner.vue';
import QxStandAlone from '@/elements/QxStandAlone.vue';
import listView from '@/components/download/filesListView.vue';
import iconView from '@/components/download/filesIconView.vue';
import EmptyList from '@/components/download/EmptyList.vue';
import ShareMessage from '@/components/download/ShareMessage';

import previewFiles from '@/elements/files/previewFiles.vue';
import { isMobile, getExtension } from '@/elements/utils.js';
import EventBus from '@/elements/eventBus.js';
import downloadMixin from '@/mixins/download.js';
import { fetchDownloadAllFilesLink, fetchFiles } from '@/api';
import { prepareURL } from '@/api/httpModule';

export default {
  name: 'DownloadPage',
  components: {
    QxButton,
    QxSpinner,
    QxTooltip,
    listView,
    iconView,
    EmptyList,
    QxStandAlone,
    ShareMessage,
    previewFiles,
  },
  mixins: [downloadMixin],
  data() {
    return {
      files: [],
      spinnerId: 'download-files-spin',
      filesView: this.getFilesView(),
      selectedFiles: [],
      isPreviewActive: false,
    };
  },
  metaInfo: {
    title: 'Download Files',
  },
  computed: {
    ...mapState({
      metadata: (state) => state.downloadPage.metadata,
    }),
    ...mapGetters({
      checkOperations: 'files/checkOperations',
      parseFile: 'files/parseModel',
    }),
    string() {
      return {
        downloadAllLabel: this.$gettext('Download all'),
        downloadSelectedLabel: this.$gettext('Download selected'),
        encryptedTootlip: this.$gettext('Encrypted share'),
        goToQuatrixLabel: this.$gettext('Open in Quatrix'),
        previewSelected: this.$gettext('Preview'),
      };
    },
    decryptionActive() {
      if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
        const ver = navigator.userAgent.match(/version\/([\.\d]+)/i);
        if (ver && parseInt(ver[1], 10) < 6) {
          return false;
        }
      }
      return this.metadata.isDownloadEncrypted;
    },
    title() {
      return this.$gettextInterpolate(
        this.$gettext('%{ senderName } (%{ senderEmail }) shared files'),
        {
          senderName: this.metadata.sender.name,
          senderEmail: this.metadata.sender.email,
        },
        true
      );
    },
    isDownloadAllowed() {
      return this.files?.some((file) => file.isDownloadAllowed);
    },
    isPreviewAllowed() {
      return this.getAllowedActions(this.selectedFiles).isPreview;
    },
    isNotIEBrowser() {
      return browser[0] !== 'MSIE';
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.getFiles(to.params.id);
    });
  },
  created() {
    this.checkDevice();
  },
  async mounted() {
    const brokenDecryptBrowser = !~navigator.userAgent.indexOf('Chrome');
    const cryptedAndDisabled = this.decryptionActive && !this.metadata.isPgpEnabled;

    if (cryptedAndDisabled) {
      this.$notify({
        text: this.$gettext("PGP disabled, you can't download encrypted files"),
        type: 'error',
        duration: -1,
      });
    }
  },
  methods: {
    ...mapMutations({
      setIsMobile: 'setIsMobile',
    }),
    async getFiles(id) {
      try {
        const { data } = await fetchFiles(id);
        this.files = (data || []).map(this.mapFileToFormat);
      } catch (error) {
        EventBus.$emit('handle-error', { response: error.response });
      } finally {
        this.spinnerId = '';
      }
    },
    mapFileToFormat(rawFile) {
      const file = this.parseFile(rawFile);
      file.isDownloadAllowed = file.download_allowed === undefined ? true : file.download_allowed;
      file.isDisabledState = !file.isDownloadAllowed;
      file.isCheckedState = false;
      file.isActiveState = false;
      file.decrypted = false;
      file.size = file.bytes;

      if (file.type !== 'F') {
        file.isDir = true;
        //For dirs property count show amount of file in folder, property size always zero
        file.size = file.count;
      } else {
        file.ftype = getExtension(file.name);
        file.nopreview = false;
        file.previewLink = prepareURL('/share/thumbnail/' + file.id);
      }
      return file;
    },
    checkDevice() {
      this.setIsMobile(isMobile());
    },
    onOpenInQuatrixClick() {
      this.$router.push({ name: 'files', params: { bc: this.metadata.folderId } });
    },
    async onDownloadAllFilesClick() {
      try {
        const {
          data: { id },
        } = await fetchDownloadAllFilesLink(this.$route.params.id);
        this.setDownloadLink(id);
      } catch ({ response }) {
        this.handleDownloadLinkError(response);
      }
    },
    getFilesView() {
      try {
        return (window.localStorage && window.localStorage.getItem('filesView')) || 'listView';
      } catch (e) {
        return 'listView';
      }
    },
    handleChangeSelected(selected) {
      this.selectedFiles = selected;
    },
    toggleView() {
      this.filesView = this.filesView === 'listView' ? 'iconView' : 'listView';
      try {
        window.localStorage.setItem('filesView', this.filesView);
      } catch (e) {}
    },
    previewSelected() {
      this.isPreviewActive = true;
    },
    getAllowedActions(list) {
      const filesList = list.map((id) => this.files.find((file) => file.id === id));
      const actions = {
        isPreview: list.length > 0,
      };
      for (const file of filesList) {
        actions.isPreview = actions.isPreview && this.isNotIEBrowser && file.isPreviewFormat && !file.isEncrypted;
      }
      return actions;
    },
  },
};
</script>
<style lang="scss" scoped>
.icon-lock {
  vertical-align: middle;
}
#share-encrypted {
  font-size: 13px;
  background: $gray-200;
  width: 95px;
  height: 18px;
  position: absolute;
  margin-left: 5px;
}
#subject {
  font-size: 18px;
  font-family: $semi-bold-sans-font;
}
.download-controls {
  button {
    height: 32px;
    font-size: 15px;
    em {
      vertical-align: text-top;
      font-size: 18px;
      margin-right: 5px;
    }
  }
}
</style>
